/**
 * Text and Media block — Video modal trigger + heart preview loop.
 *
 * Clicking a `.rmh-video-play-btn` opens the shared video modal (see
 * src/video-modal.js). Heart-orientation uploaded video previews render as
 * muted looping `<video>` elements with IntersectionObserver-based lazy
 * playback and a configurable loop boundary (data-loop-max).
 */

import { initVideoModal, isModalOpen } from '@/video-modal';

/** @type {Set<HTMLVideoElement>} Preview videos currently visible in viewport. */
const visiblePreviews = new Set();

/** @type {Set<HTMLVideoElement>} All managed preview <video> elements. */
const allPreviews = new Set();

/**
 * Initialize heart video preview loops for uploaded MP4 <video> elements.
 *
 * Sets up IntersectionObserver for lazy play/pause and timeupdate
 * listeners to enforce the loop boundary (data-loop-max).
 */
function initPreviewLoops() {
	const videos = document.querySelectorAll(
		'.rmh-text-and-media__video-heart video[data-loop-max]'
	);

	if (!videos.length) return;

	// Respect reduced motion preference — remove autoplay and pause on first frame.
	const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
	if (reducedMotion) {
		for (const video of videos) {
			video.pause();
		}
		return;
	}

	// Time-bounded loop via timeupdate.
	for (const video of videos) {
		allPreviews.add(video);

		const loopMax = parseFloat(video.dataset.loopMax) || 8;

		video.addEventListener('timeupdate', () => {
			if (video.currentTime >= loopMax) {
				video.currentTime = 0;
			}
		});
	}

	// IntersectionObserver for lazy playback.
	if ('IntersectionObserver' in window) {
		const observer = new IntersectionObserver(
			(entries) => {
				for (const entry of entries) {
					const video = /** @type {HTMLVideoElement} */ (entry.target);

					if (entry.isIntersecting) {
						visiblePreviews.add(video);
						if (!isModalOpen()) {
							video.play().catch(() => {});
						}
					} else {
						visiblePreviews.delete(video);
						video.pause();
					}
				}
			},
			{ threshold: 0.25 }
		);

		for (const video of videos) {
			video.pause();
			observer.observe(video);
		}
	} else {
		for (const video of videos) {
			visiblePreviews.add(video);
			video.pause();
			video.play().catch(() => {});
		}
	}
}

/**
 * Pause all preview videos when the modal opens; resume visible ones on close.
 */
function bindModalEvents() {
	document.addEventListener('rmh:video-modal-open', () => {
		for (const video of allPreviews) {
			video.pause();
		}
	});

	document.addEventListener('rmh:video-modal-close', () => {
		for (const video of visiblePreviews) {
			video.play().catch(() => {});
		}
	});
}

/**
 * Initialize play button click handlers and preview loops.
 */
document.addEventListener('DOMContentLoaded', () => {
	initVideoModal();
	bindModalEvents();
	initPreviewLoops();
});
